import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, Clock, Tag } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ArticleReadingProgress } from "@/components/ArticleReadingProgress";
import { ArticleTOC, sectionId } from "@/components/ArticleTOC";
import { COMPANY } from "@/lib/company";
import { INSIGHT_CARDS } from "@/content/insights/cards.gen";
import { INSIGHT_CATEGORY_LABEL } from "@/content/insights/labels";
import { INSIGHT_HERO_IMAGES, insightHeroAbsolute } from "@/content/insights/hero-images";
import { SmartImage } from "@/components/SmartImage";
import { loadInsight } from "@/content/insights/lazy.gen";
import { getEnInsightSlugForThaiSlug } from "@/content/en-insights/pair.gen";
import type { InsightPost } from "@/content/insights/types";
import { getActiveProvince } from "@/provinces/_registry";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildFaqSchema,
} from "@/lib/schema";
import { DEFAULT_OG_IMAGE } from "@/lib/seo";

export const Route = createFileRoute("/insights/$slug")({
  component: InsightDetail,
  loader: async ({ params }) => {
    const post = await loadInsight(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold">ไม่พบบทความ</h1>
        <p className="mt-3 text-muted-foreground">
          บทความที่คุณค้นหาอาจถูกย้ายหรือเปลี่ยน URL
        </p>
        <Link
          to="/insights"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-bold text-primary-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> กลับไปหน้าบทความ
        </Link>
      </main>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-background p-12 text-center">
      <h1 className="text-xl font-bold">เกิดข้อผิดพลาด</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
  head: ({ params, loaderData }) => {
    const post = loaderData?.post;
    const { config } = getActiveProvince();
    const site = getActiveSiteUrl();
    if (!post) {
      return pageHeadForProvince({
        title: "ไม่พบบทความ — Thai Notary",
        description: "ไม่พบบทความที่ค้นหา",
        path: `/insights/${params.slug}`,
      });
    }
    const url = `${site}/insights/${post.slug}`;
    // Word count + ArticleBody snippet for BlogPosting schema
    const bodyText = post.sections
      .map((s) => [s.h2, ...s.body, ...(s.bullets ?? [])].join(" "))
      .join(" ");
    const wordCount = bodyText.split(/\s+/).filter(Boolean).length;
    const head = pageHeadForProvince({
      title: post.metaTitle,
      description: post.metaDesc,
      path: `/insights/${post.slug}`,
      type: "article",
      image: post.heroImage ?? insightHeroAbsolute(post.category, site),
      alternates: (() => {
        const enSlug = getEnInsightSlugForThaiSlug(post.slug);
        return enSlug ? [{ lang: "en", path: `/en/insights/${enSlug}` }] : [];
      })(),
    });
    const scripts: { type: string; children: string }[] = [
      { type: "application/ld+json", children: JSON.stringify(buildOrganizationSchema()) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildBreadcrumbSchema([
            { name: "หน้าแรก", url: site },
            { name: "บทความ", url: `${site}/insights` },
            { name: post.title, url },
          ]),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.metaDesc,
          datePublished: post.publishedAt,
          dateModified: post.updatedAt ?? post.publishedAt,
          author: { "@type": "Organization", name: COMPANY.brandName },
          publisher: {
            "@type": "Organization",
            name: COMPANY.brandName,
            logo: { "@type": "ImageObject", url: `${site}/favicon.webp` },
          },
          image: post.heroImage ?? insightHeroAbsolute(post.category, site),
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
          articleSection: INSIGHT_CATEGORY_LABEL[post.category],
          keywords: post.tags.join(", "),
          inLanguage: "th-TH",
          about: { "@type": "Place", name: config.nameEn },
          wordCount,
          timeRequired: `PT${post.readingMinutes}M`,
          articleBody: bodyText.slice(0, 500),
          speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: ["h1", ".tldr-speakable"],
          },
        }),
      },
    ];
    if (post.faqs?.length) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify(buildFaqSchema(post.faqs)),
      });
    }
    return { ...head, scripts };
  },
});

function InsightDetail() {
  const { post } = Route.useLoaderData() as { post: InsightPost };
  const related = INSIGHT_CARDS.filter(
    (p) => p.slug !== post.slug && p.category === post.category,
  ).slice(0, 3);
  return (
    <div className="min-h-screen bg-background text-foreground">
      <ArticleReadingProgress />
      <SiteHeader />
      <main id="main-content">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-12 lg:grid-cols-[1fr_220px]">
        <article>
          <Link
            to="/insights"
            className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary"
          >
            <ArrowLeft className="h-3 w-3" /> บทความทั้งหมด
          </Link>
          <div className="mt-4 flex items-center gap-2 text-xs font-semibold text-gold">
            <Tag className="h-3 w-3" />
            {INSIGHT_CATEGORY_LABEL[post.category]}
          </div>
          <h1 className="mt-2 text-3xl font-extrabold leading-tight tracking-tight md:text-4xl">
            {post.title}
          </h1>
          <figure className="mt-6 overflow-hidden rounded-2xl border border-border">
            <SmartImage
              src={post.heroImage ?? INSIGHT_HERO_IMAGES[post.category].src}
              alt={INSIGHT_HERO_IMAGES[post.category].altTh}
              width={1200}
              height={630}
              priority
              className="h-auto w-full object-cover"
            />
          </figure>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
            <span>
              เผยแพร่{" "}
              {new Date(post.publishedAt).toLocaleDateString("th-TH", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
            {post.updatedAt && (
              <span>
                อัปเดต{" "}
                {new Date(post.updatedAt).toLocaleDateString("th-TH", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" /> {post.readingMinutes} นาที
            </span>
          </div>

          <div className="mt-8 rounded-xl border border-gold/30 bg-gold/5 p-5">
            <div className="text-xs font-bold uppercase tracking-wider text-gold">
              TL;DR
            </div>
            <p className="tldr-speakable mt-2 text-sm leading-relaxed text-foreground">
              {post.tldr}
            </p>
          </div>

          <div className="mt-10 space-y-10">
            {post.sections.map((s, idx) => (
              <section key={idx} id={sectionId(s, idx)} className="scroll-mt-24">
                <h2 className="text-xl font-bold md:text-2xl">{s.h2}</h2>
                {s.body.map((para, i) => (
                  <p
                    key={i}
                    className="mt-3 text-base leading-relaxed text-foreground/90"
                  >
                    {para}
                  </p>
                ))}
                {s.bullets && (
                  <ul className="mt-4 space-y-2">
                    {s.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>


          {post.downloads && post.downloads.length > 0 && (
            <section className="mt-8 rounded-xl border border-primary/30 bg-primary/5 p-5">
              <h2 className="text-base font-bold">เอกสารดาวน์โหลด</h2>
              <ul className="mt-3 space-y-3">
                {post.downloads.map((d) => (
                  <li key={d.href}>
                    <a
                      href={d.href}
                      download
                      className="text-sm font-semibold text-primary underline underline-offset-4"
                    >
                      {d.label}
                    </a>
                    {d.note && (
                      <div className="mt-1 text-xs text-muted-foreground">{d.note}</div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {post.faqs && post.faqs.length > 0 && (
            <section className="mt-12 border-t border-border pt-10">
              <h2 className="text-xl font-bold md:text-2xl">คำถามที่พบบ่อย</h2>
              <div className="mt-4 space-y-4">
                {post.faqs.map((f, i) => (
                  <div
                    key={i}
                    className="rounded-lg border border-border bg-card p-4"
                  >
                    <div className="font-semibold text-foreground">{f.q}</div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {f.a}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {post.relatedServiceSlugs && post.relatedServiceSlugs.length > 0 && (
            <section className="mt-12 border-t border-border pt-10">
              <h2 className="text-lg font-bold">บริการที่เกี่ยวข้อง</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {post.relatedServiceSlugs.map((s) => (
                  <Link
                    key={s}
                    to="/services/$service"
                    params={{ service: s }}
                    className="rounded-full border border-primary/40 bg-primary/5 px-3 py-1 text-xs font-semibold text-primary hover:bg-primary hover:text-primary-foreground"
                  >
                    {s}
                  </Link>
                ))}
              </div>
            </section>
          )}

          <section className="mt-12 rounded-xl bg-primary p-6 text-primary-foreground">
            <h2 className="text-lg font-bold">มีคำถามเฉพาะเคสของคุณ?</h2>
            <p className="mt-1 text-sm opacity-90">
              ปรึกษาทีมทนายฟรี 15 นาที — ตอบกลับใน LINE ภายใน 30 นาที
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <a
                href={`tel:${COMPANY.phoneIntl}`}
                className="rounded-md bg-gold px-4 py-2 text-sm font-bold text-gold-foreground"
              >
                📞 {COMPANY.phone}
              </a>
              <a
                href={`https://line.me/R/ti/p/${COMPANY.lineId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md border border-primary-foreground/40 px-4 py-2 text-sm font-bold"
              >
                LINE {COMPANY.lineId}
              </a>
            </div>
          </section>
        </article>
        <aside>
          <ArticleTOC sections={post.sections} />
        </aside>
        </div>

        {related.length > 0 && (
          <section className="border-t border-border bg-muted/30">
            <div className="mx-auto max-w-5xl px-4 py-12">
              <h2 className="mb-6 text-xl font-bold">บทความที่เกี่ยวข้อง</h2>
              <div className="grid gap-4 md:grid-cols-3">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to="/insights/$slug"
                    params={{ slug: r.slug }}
                    className="group rounded-lg border border-border bg-card p-4 transition hover:border-primary"
                  >
                    <div className="text-xs font-semibold text-gold">
                      {INSIGHT_CATEGORY_LABEL[r.category]}
                    </div>
                    <div className="mt-2 font-bold leading-snug group-hover:text-primary">
                      {r.title}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}